import * as React from 'react';
import type { DeliveryVoucher } from 'contracts/DeliveryVouchers';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Textarea } from 'components/ui/textarea';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components';

export type RejectDeliveryVoucherDialogRef = {
  open: (voucher: DeliveryVoucher) => void;
  close: () => void;
};

type Props = {
  loading?: boolean;
  onSubmit: (voucher: DeliveryVoucher, reason: string) => void;
};

export const RejectDeliveryVoucher = React.forwardRef<
  RejectDeliveryVoucherDialogRef,
  Props
>(({ loading = false, onSubmit }, ref) => {
  const [selectedVoucher, setSelectedVoucher] = React.useState<DeliveryVoucher>();
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const closeDialog = React.useCallback(() => {
    setSelectedVoucher(undefined);
    setReason('');
    setError(null);
  }, []);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (nextOpen || loading) return;

      closeDialog();
    },
    [closeDialog, loading]
  );

  const handleSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const trimmedReason = reason.trim();
      if (!selectedVoucher || loading) return;

      if (!trimmedReason) {
        setError('Informe a justificativa da reprovação.');
        return;
      }

      onSubmit(selectedVoucher, trimmedReason);
    },
    [loading, onSubmit, reason, selectedVoucher]
  );

  React.useImperativeHandle(
    ref,
    () => ({
      open: voucher => {
        setSelectedVoucher(voucher);
        setReason('');
        setError(null);
      },
      close: closeDialog,
    }),
    [closeDialog]
  );

  return (
    <Dialog open={Boolean(selectedVoucher)} onOpenChange={handleOpenChange}>
      <DialogContent className="w-full max-w-[540px]">
        <DialogHeader>
          <DialogTitle>Reprovar comprovante</DialogTitle>
          <DialogDescription>
            Informe a justificativa para reprovar este comprovante.
          </DialogDescription>
        </DialogHeader>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-2">
            <Textarea
              value={reason}
              disabled={loading}
              placeholder="Justificativa da reprovação"
              className="min-h-[120px] resize-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
              onChange={event => {
                setReason(event.target.value);
                setError(null);
              }}
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>

          <DialogFooter className="grid grid-cols-2 gap-4 sm:space-x-0">
            <Button.Root
              type="button"
              size="sm"
              variant="secondary"
              disabled={loading}
              onClick={closeDialog}
            >
              Cancelar
            </Button.Root>
            <Button.Root
              type="submit"
              size="sm"
              variant="danger"
              disabled={loading}
            >
              {loading ? <ActivityIndicator /> : 'Reprovar'}
            </Button.Root>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

RejectDeliveryVoucher.displayName = 'RejectDeliveryVoucher';
